import { FC, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { useGetCreditBalancesQuery } from '@/services/endpoints/credits';
import { CampaignCreditChannel, CREDIT_TYPE_BY_CHANNEL, PACKAGES_ROUTE_BY_CHANNEL } from '../config';
import { formatCreditDate, formatCredits } from '../utils/formatCredits';
import { isCreditsFeatureUnavailable } from '../utils/isCreditsFeatureUnavailable';
import { cn } from '@/lib/utils';
import { Button } from '@/lib';

const MINIMUM_LOADING_DURATION = 300;

type CampaignsCreditBalanceBarProps = {
  channel: CampaignCreditChannel;
};
const TONE_CLASSES = {
  out: {
    border: 'border-[#f3a79e]',
    bg: 'bg-[#fff0f0]',
    text: 'text-[#96271e]',
    requestCta: 'text-[#96271e]',
  },
  default: {
    border: 'border-secondary-border',
    bg: 'bg-white-100',
    text: 'text-slate-900',
    requestCta: 'text-brand-600 hover:text-brand-700',
  },
} as const;

export const CampaignsCreditBalanceBar: FC<CampaignsCreditBalanceBarProps> = ({ channel }) => {
  const { t, i18n } = useTranslation('campaign');
  const history = useHistory();
  const workspace = useWorkspace();
  const creditType = CREDIT_TYPE_BY_CHANNEL[channel];

  const { data, isLoading, isFetching, isError, error, isUninitialized } = useGetCreditBalancesQuery(
    { workspaceId: workspace.id, type: creditType },
    { skip: !workspace.id }
  );

  const isLoadingDelayed = useDelayedBoolean(isLoading || isFetching, MINIMUM_LOADING_DURATION);

  const balance = useMemo(() => {
    if (!data?.length || isLoading || isFetching || isUninitialized) {
      return null;
    }

    return data.find((item) => item.creditType === creditType) ?? data[0] ?? null;
  }, [data, creditType, isLoading, isFetching, isUninitialized]);

  if (isCreditsFeatureUnavailable(error)) {
    return null;
  }

  if (isError) {
    return null;
  }

  const {
    available = 0,
    reserved = 0,
    grace = 0,
    capacity = null,
    expirySchedule = [],
  } = balance ?? {
    available: 0,
    reserved: 0,
    grace: 0,
    capacity: null,
    expirySchedule: [],
  };

  const firstLot = expirySchedule?.[0];
  const isOut = available <= 0;

  const hasCapacity = capacity != null && (capacity.exact != null || (capacity.min != null && capacity.max != null));
  const hasExactCapacity = capacity?.exact != null;

  const heroValue = hasCapacity
    ? hasExactCapacity
      ? formatCredits(capacity.exact!, i18n.language)
      : `${formatCredits(capacity!.min!, i18n.language)}–${formatCredits(capacity!.max!, i18n.language)}`
    : formatCredits(available, i18n.language);

  const heroLabel = hasCapacity
    ? hasExactCapacity
      ? t('credits.balance.messagesExactLabel')
      : t('credits.balance.messagesRangeLabel')
    : t('credits.balance.availableLabel');

  const warningNote = isOut ? t('credits.balance.outWarning', { amount: formatCredits(grace, i18n.language) }) : null;
  const reservedNote = reserved > 0 ? t('credits.balance.reservedLine', { amount: formatCredits(reserved, i18n.language) }) : t('credits.balance.noReserved');

  const showLoading = isUninitialized || isLoading || isLoadingDelayed;

  const tone = isOut ? 'out' : 'default';

  return (
    <Loadable isLoading={showLoading}>
      {balance ? (
        <div className={cn('flex flex-wrap items-center gap-6 rounded-lg border p-4', TONE_CLASSES[tone].border, TONE_CLASSES[tone].bg)}>
          <div className='flex min-w-[200px] flex-col gap-0.5'>
            <div className='flex flex-wrap items-baseline gap-2'>
              <span className={cn(TONE_CLASSES[tone].text, 'text-xl font-bold')}>{heroValue}</span>
              <span className='text-xs font-medium text-[#46505f]'>{heroLabel}</span>
            </div>
            {hasCapacity ? (
              <p className={cn('text-slate-500 text-sm')}>{t('credits.balance.creditsAvailableLine', { amount: formatCredits(available, i18n.language) })}</p>
            ) : null}
          </div>

          <div className='hidden h-10 w-px self-stretch bg-secondary-border sm:block' />

          <div className='flex min-w-[220px] flex-1 flex-col gap-1'>
            <p className='text-sm text-[#46505f]'>{reservedNote}</p>
            {firstLot ? (
              <p className='text-[12.5px] text-slate-500'>
                {t('credits.balance.expiryLine', {
                  amount: formatCredits(firstLot.quantity, i18n.language),
                  date: formatCreditDate(firstLot.expiresAt, i18n.language),
                })}
              </p>
            ) : null}
            {warningNote ? <p className={cn('text-[12.5px] font-medium', TONE_CLASSES[tone].text)}>{warningNote}</p> : null}
          </div>

          <Button
            intent='link-color'
            size='sm'
            className={cn('underline whitespace-nowrap hover:opacity-80 hover:text-red-800', TONE_CLASSES[tone].requestCta, {
              '!text-[#fff] bg-red-600 hover:bg-red-700 rounded-md !p-4 min-w-fit !no-underline': tone === 'out',
            })}
            onClick={() => history.push(PACKAGES_ROUTE_BY_CHANNEL[channel])}
          >
            {t('credits.balance.requestCta')}
          </Button>
        </div>
      ) : null}
    </Loadable>
  );
};
